// src/components/site-editor/panels/NewsletterPanel.js
import React from 'react';
import { Mail, User, Info } from 'lucide-react';
import { useWebsiteBuilder } from '../../../contexts/WebsiteBuilderContext';
import InlineCallout from '../InlineCallout';
import Toggle from '../../Toggle';

const NewsletterPanel = () => {
  const { state, actions } = useWebsiteBuilder();
  const newsletter = state.draft.marketing.newsletter || {
    enabled: false,
    headline: 'Stay Connected',
    description: 'Sign up to receive updates on new offerings, insights, and upcoming events.',
    layout: 'centered-box',
    showNameField: true
  };

  const handleUpdate = (field, value) => {
    actions.updateMarketing('newsletter', {
      ...newsletter,
      [field]: value
    });
  };

  const handleToggleVisibility = () => {
    handleUpdate('enabled', !newsletter.enabled);
  };

  const handleToggleNameField = () => {
    handleUpdate('showNameField', !newsletter.showNameField);
  };

  const handleLayoutSelect = (layout) => {
    handleUpdate('layout', layout);
  };

  // Layout options with preview illustrations
  const layoutOptions = [
    {
      id: 'centered-box',
      name: 'Centered Box',
      description: 'Clean, contained card — the most common choice',
      preview: (
        <svg viewBox="0 0 120 80" className="w-full h-auto">
          <rect width="120" height="80" fill="#F9FAFB" />
          <rect x="20" y="15" width="80" height="50" rx="4" fill="white" stroke="#E5E7EB" strokeWidth="1" />
        </svg>
      )
    },
    {
      id: 'full-width',
      name: 'Full-Width Banner',
      description: 'Bold, prominent — grabs attention',
      preview: (
        <svg viewBox="0 0 120 80" className="w-full h-auto">
          <rect width="120" height="80" fill="#F9FAFB" />
          <rect x="5" y="25" width="110" height="30" rx="2" fill="white" stroke="#E5E7EB" strokeWidth="1" />
        </svg>
      )
    },
    {
      id: 'minimal',
      name: 'Minimal Inline',
      description: 'Compact single-line form — subtle and unobtrusive',
      preview: (
        <svg viewBox="0 0 120 80" className="w-full h-auto">
          <rect width="120" height="80" fill="#F9FAFB" />
          <rect x="15" y="35" width="90" height="10" rx="1" fill="white" stroke="#E5E7EB" strokeWidth="1" />
        </svg>
      )
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Newsletter</h2>
        <p className="text-sm text-gray-600">
          Add a signup form to your website to start building your email list.
        </p>
      </div>

      {/* Visibility Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div>
          <div className="text-sm font-medium text-gray-900">Show Newsletter on website</div>
          <div className="text-xs text-gray-600">Toggle to hide/show this section</div>
        </div>
        <Toggle
          enabled={newsletter.enabled}
          onChange={handleToggleVisibility}
        />
      </div>

      {/* Playbook Callout */}
      <InlineCallout
        text="An email list is one of the most valuable tools for growing your practice. Start collecting signups now — even before connecting an email service."
        playbookTopic="marketing-and-growth"
      />

      {/* Content Fields */}
      <div className="space-y-4">
        {/* Headline */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Headline
          </label>
          <input
            type="text"
            value={newsletter.headline}
            onChange={(e) => handleUpdate('headline', e.target.value)}
            maxLength={80}
            placeholder="e.g., Stay Connected, Join My Community"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <div className="text-xs text-gray-500 mt-1">
            {newsletter.headline.length} / 80 characters
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Description
          </label>
          <textarea
            value={newsletter.description}
            onChange={(e) => handleUpdate('description', e.target.value)}
            maxLength={200}
            rows={3}
            placeholder="Tell visitors what they'll get by subscribing..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
          />
          <div className="text-xs text-gray-500 mt-1">
            {newsletter.description.length} / 200 characters
          </div>
        </div>
      </div>

      {/* Form Fields Configuration */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Form Fields</h3>

        {/* Email field (always included) */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <Mail className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-600">Email address — Always included</span>
        </div>

        {/* Name field toggle */}
        <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
          <div className="flex items-center gap-3">
            <User className="w-4 h-4 text-gray-600" />
            <div>
              <div className="text-sm font-medium text-gray-900">Include name field</div>
              <div className="text-xs text-gray-600">
                {newsletter.showNameField
                  ? "Subscribers can optionally provide their name for personalized emails"
                  : "Email-only signup — simpler form, higher conversion"
                }
              </div>
            </div>
          </div>
          <Toggle
            enabled={newsletter.showNameField}
            onChange={handleToggleNameField}
          />
        </div>
      </div>

      {/* Layout Selector */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Layout Style</h3>
        <div className="grid grid-cols-3 gap-3">
          {layoutOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => handleLayoutSelect(option.id)}
              className={`p-3 border-2 rounded-lg transition-all ${
                newsletter.layout === option.id
                  ? 'border-purple-600 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="mb-2 bg-gray-50 rounded p-2">
                {option.preview}
              </div>
              <div className="text-xs font-semibold text-gray-900">{option.name}</div>
              <div className="text-xs text-gray-600 mt-1">{option.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Integration Placeholder */}
      <div className="flex gap-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <Info className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-gray-600 leading-relaxed">
          Email service integration (Mailchimp, ConvertKit, and more) is coming soon. Any signups collected now will be ready to export when you connect your service.
        </div>
      </div>
    </div>
  );
};

export default NewsletterPanel;
